/**
 * Lightweight TypeScript types that mirror the Python/Pydantic API shapes.
 * No calculation logic - just interfaces for the REST API contract.
 */

// ─── Core Types ──────────────────────────────────────────────

export type Quarter = string;

export type QuarterlyData = Record<string, number>;

export type FiscalYear = number;

const MONTH_ABBREVS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function formatQuarter(q: Quarter): string {
  const [prefix, year] = q.split('_');
  if (prefix.startsWith('M')) {
    const monthIdx = parseInt(prefix.slice(1), 10) - 1;
    return `${MONTH_ABBREVS[monthIdx]} ${year}`;
  }
  if (prefix === 'FY') {
    return `FY ${year}`;
  }
  return `${prefix} ${year}`;
}

// ─── Forecast Context ───────────────────────────────────────

export interface ForecastContext {
  allQuarters: string[];
  fiscalYears: number[];
  quartersByYear: Record<number, string[]>;
  numQuarters: number;
  periodsPerYear: number;
  periodType: string;
  lastQuarter: string;
  firstQuarter: string;
}

export function getForecastContext(settings: ModelSettings): ForecastContext {
  const startYear = settings.forecastStartYear ?? 2026;
  const numYears = settings.numForecastYears ?? 4;
  const periodType = settings.periodType ?? 'quarterly';
  const fiscalYears: number[] = [];
  const allQuarters: string[] = [];
  const quartersByYear: Record<number, string[]> = {};

  const periodsPerYear = periodType === 'monthly' ? 12 : periodType === 'annual' ? 1 : 4;

  for (let y = startYear; y < startYear + numYears; y++) {
    fiscalYears.push(y);
    let yearPeriods: string[];
    if (periodType === 'monthly') {
      yearPeriods = Array.from({ length: 12 }, (_, i) => `M${String(i + 1).padStart(2, '0')}_${y}`);
    } else if (periodType === 'annual') {
      yearPeriods = [`FY_${y}`];
    } else {
      yearPeriods = [`Q1_${y}`, `Q2_${y}`, `Q3_${y}`, `Q4_${y}`];
    }
    allQuarters.push(...yearPeriods);
    quartersByYear[y] = yearPeriods;
  }

  return {
    allQuarters,
    fiscalYears,
    quartersByYear,
    numQuarters: allQuarters.length,
    periodsPerYear,
    periodType,
    lastQuarter: allQuarters[allQuarters.length - 1],
    firstQuarter: allQuarters[0],
  };
}

export function getDefaultForecastContext(): ForecastContext {
  return getForecastContext({ forecastStartYear: 2026, numForecastYears: 4, periodType: 'quarterly' } as ModelSettings);
}

// ─── Sheet Definitions ───────────────────────────────────────

export type SheetId =
  | 'overview'
  | 'pnl'
  | 'cashflows'
  | 'balance-sheet'
  | 'debt-schedule'
  | 'staffing'
  | 'control-account'
  | 'sensitivity'
  | 'what-if'
  | 'annual-summary'
  | 'charts'
  | 'org-chart'
  | 'assumptions'
  | 'historical-data'
  | 'backtest'
  | 'import-report';

export interface SheetMeta {
  id: SheetId;
  label: string;
  description: string;
  hasInputs: boolean;
}

export const SHEET_DEFINITIONS: SheetMeta[] = [
  { id: 'overview', label: 'Overview', description: 'Model overview and table of contents', hasInputs: false },
  { id: 'pnl', label: 'P&L', description: 'Profit & Loss Statement', hasInputs: true },
  { id: 'cashflows', label: 'Cashflows', description: 'Cash Flow Statement', hasInputs: true },
  { id: 'balance-sheet', label: 'Balance Sheet', description: 'Balance Sheet', hasInputs: true },
  { id: 'debt-schedule', label: 'Debt Schedule', description: 'Debt Schedule & Interest', hasInputs: true },
  { id: 'staffing', label: 'Staffing', description: 'Staff Headcount & Costs', hasInputs: true },
  { id: 'control-account', label: 'Control Account', description: 'Control Account', hasInputs: true },
  { id: 'sensitivity', label: 'Sensitivity', description: 'Sensitivity Analysis', hasInputs: true },
  { id: 'what-if', label: 'What-If', description: 'What-If Scenario Analysis', hasInputs: true },
  { id: 'annual-summary', label: 'Annual Summary', description: 'Annual P&L Summary', hasInputs: false },
  { id: 'charts', label: 'Charts', description: 'Interactive financial charts', hasInputs: false },
  { id: 'org-chart', label: 'Org Chart', description: 'Organization Chart', hasInputs: false },
  { id: 'assumptions', label: 'Assumptions', description: 'Model Assumptions', hasInputs: true },
  { id: 'historical-data', label: 'Historical Data', description: 'Prior Year Actuals (P&L, Balance Sheet, Cashflow)', hasInputs: false },
  { id: 'backtest', label: 'Backtest', description: 'Validate forecast methodology against a held-out historical year', hasInputs: false },
];

// ─── Sensitivity Config ─────────────────────────────────────

export interface SensitivityScenario {
  variable: string;
  shockPercent: number;
}

export interface SensitivityConfig {
  scenarios: SensitivityScenario[];
}

export const SENSITIVITY_VARIABLES = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'costs', label: 'Total Costs' },
  { key: 'cogs_ratio', label: 'COGS Ratio' },
  { key: 'interest_rate', label: 'Interest Rate' },
  { key: 'tax_rate', label: 'Tax Rate' },
  { key: 'staff_costs', label: 'Staff Costs' },
  { key: 'headcount', label: 'Headcount' },
  { key: 'fx_rate', label: 'FX Rate (AED)' },
] as const;

// ─── What-If Config ─────────────────────────────────────────

export interface WhatIfOverrides {
  revenueScaleFactor: number;
  headcountScaleFactor: number;
  opexScaleFactor: number;
  cogsRatio: number | null;
  interestRate: number | null;
  taxRate: number | null;
  staffWelfareRate: number | null;
  fxRateAED: number | null;
}

export interface WhatIfScenario {
  name: string;
  overrides: WhatIfOverrides;
}

export interface WhatIfConfig {
  scenarios: WhatIfScenario[];
}

export const WHAT_IF_SCALE_VARIABLES = [
  { key: 'revenueScaleFactor' as const, label: 'Revenue' },
  { key: 'headcountScaleFactor' as const, label: 'Headcount' },
  { key: 'opexScaleFactor' as const, label: 'OpEx' },
];

export const WHAT_IF_SETTING_VARIABLES = [
  { key: 'cogsRatio' as const, label: 'COGS Ratio' },
  { key: 'interestRate' as const, label: 'Interest Rate' },
  { key: 'taxRate' as const, label: 'Tax Rate' },
  { key: 'staffWelfareRate' as const, label: 'Staff Welfare Rate' },
  { key: 'fxRateAED' as const, label: 'FX Rate (AED)' },
];

// ─── Valuation Config ───────────────────────────────────────

export type ValuationMethod = 'dcf' | 'multiples' | 'both';

export interface ValuationConfig {
  method: ValuationMethod;
  discountRate: number;
  terminalGrowthRate: number;
  exitMultipleEVEBITDA: number;
  compEVRevenue: number | null;
  compEVEBITDA: number | null;
  compPE: number | null;
  sharesOutstanding: number;
  terminalYear: number;
  /** Corpus peers the user has taken out (company numbers). */
  excludedPeers?: string[];
  /** "fixed": discountRate as entered; "build_up": built from a gilt, a beta and the rest. */
  discountRateMode?: 'fixed' | 'build_up';
  /** Parts of the build-up the user supplies, each winning over the built one. */
  discountRateInputs?: Record<string, number>;
  /** "YYYY-MM": the month whose gilt yield is the risk-free rate. */
  valuationMonth?: string | null;
  /** Set by the build-up: residual income and dividends discount at it. */
  costOfEquity?: number | null;
  /** Residual income's continuing value. */
  residualIncomeForm?: 'gordon' | 'persistence';
  /**
   * What the business runs on, which decides which valuation methods are its
   * anchors. See services/valuator/app/valuation/v2/methods.py for the values.
   * v2 only; v1 models simply do not carry it.
   */
  businessType?: BusinessType;
}

/** The business types the method framework knows. */
export type BusinessType =
  | 'general'
  | 'bank'
  | 'insurer'
  | 'manufacturer'
  | 'saas'
  | 'reit'
  | 'mining'
  | 'biotech'
  | 'asset_manager'
  | 'conglomerate'
  | 'distressed'
  | 'utility'
  | 'startup'
  | 'high_growth_tech'
  | 'commodity_holder';

// ─── Model Settings ──────────────────────────────────────────

export interface ModelSettings {
  baseCurrency: string;
  fxRateAED: number;
  cogsRatio: number;
  interestRate: number;
  depreciationPeriodMonths: number;
  taxRate: number;
  staffWelfareRate: number;
  forecastStartYear: number;
  numForecastYears: number;
  periodType: 'quarterly' | 'monthly' | 'annual';
  historicalYears?: number[];
  /** Forecast goods sold as a share of revenue; null/0 = the last filed year's margin. */
  forecastCogsRatio?: number | null;
  /** Forecast staff welfare as a share of indirect staff costs; null/0 = none. */
  forecastWelfareRate?: number | null;
  sensitivityConfig: SensitivityConfig;
  whatIfConfig: WhatIfConfig;
  valuationConfig: ValuationConfig;
  /**
   * Derive deferred tax from the fixed-asset timing difference instead of
   * carrying the filed balance forward. v2 only.
   */
  deriveDeferredTax?: boolean;
  /** Writing-down allowance, reducing balance. Only read when deriving. */
  capitalAllowanceRate?: number;
  /**
   * A tax rate for particular years, overriding `taxRate` where given. Keyed by
   * the fiscal year: { "2023": 0.19, "2024": 0.25 }.
   */
  taxRateByYear?: Record<string, number>;
  /**
   * Unwind the lease liability with interest and split the payment into
   * interest and capital, instead of treating its whole movement as a payment.
   */
  deriveLeaseSchedule?: boolean;
  /** The incremental borrowing rate the lease was discounted at. */
  leaseDiscountRate?: number;
  /** The remaining term, used to pay the liability down. */
  leaseTermMonths?: number;
  /**
   * Charge interest on the borrowings balance and roll it with the drawdowns and
   * repayments, instead of holding the filed interest flat.
   */
  deriveDebtSchedule?: boolean;
  /** The rate the borrowings bear. Only read when deriving. */
  debtInterestRate?: number;
}

// ─── Model Inputs ────────────────────────────────────────────

export interface RevenueInputs {
  sales1: QuarterlyData;
  sales2: QuarterlyData;
  sales3: QuarterlyData;
  otherRevenue: QuarterlyData;
}

export interface ExpenseInputs {
  websiteDesign: QuarterlyData;
  marketing: QuarterlyData;
  travelCosts: QuarterlyData;
  entertainment: QuarterlyData;
  officeRent: QuarterlyData;
  utilities: QuarterlyData;
  insurance: QuarterlyData;
  telephone: QuarterlyData;
  printing: QuarterlyData;
  repairs: QuarterlyData;
  crm: QuarterlyData;
  cloud: QuarterlyData;
  bankCharges: QuarterlyData;
  audit: QuarterlyData;
  legal: QuarterlyData;
}

export interface StaffingDepartment {
  headcount: QuarterlyData;
  costPerHead: QuarterlyData;
}

export interface StaffingInputs {
  operationsRiskTech: StaffingDepartment;
  executiveHRAdmin: StaffingDepartment;
  finance: StaffingDepartment;
  structuringInvestment: StaffingDepartment;
  productSales: StaffingDepartment;
}

export interface DebtInputs {
  loanDrawdowns: QuarterlyData;
  loanRepayments: QuarterlyData;
}

export interface ControlAccountInputs {
  equityInvestment: QuarterlyData;
  fixedAssetAdditions: QuarterlyData;
  debtorDays: number;
  creditorDays: number;
  vatRate: number;
  dividends: QuarterlyData;
}

export interface ModelInputs {
  revenue: RevenueInputs;
  expenses: ExpenseInputs;
  staffing: StaffingInputs;
  debtSchedule: DebtInputs;
  controlAccount: ControlAccountInputs;
}

// ─── Model Computed ──────────────────────────────────────────

export interface PnLComputed {
  totalRevenue: QuarterlyData;
  cogs: QuarterlyData;
  grossProfit: QuarterlyData;
  totalStaffCosts: QuarterlyData;
  staffWelfare: QuarterlyData;
  totalStaffExpense: QuarterlyData;
  totalMarketing: QuarterlyData;
  totalTravel: QuarterlyData;
  totalAdmin: QuarterlyData;
  totalTechnical: QuarterlyData;
  totalFinanceLegal: QuarterlyData;
  totalIndirectCosts: QuarterlyData;
  ebitda: QuarterlyData;
  depreciation: QuarterlyData;
  depreciationAdj: QuarterlyData;
  interestExpense: QuarterlyData;
  profitBeforeTax: QuarterlyData;
  corporationTax: QuarterlyData;
  profitAfterTax: QuarterlyData;
  retainedProfitBF: QuarterlyData;
  retainedProfitCF: QuarterlyData;
}

export interface CashflowComputed {
  operatingProfit: QuarterlyData;
  addBackDepreciation: QuarterlyData;
  adjDepreciation: QuarterlyData;
  changeInDebtors: QuarterlyData;
  changeInCreditors: QuarterlyData;
  changeInVat: QuarterlyData;
  corporationTaxPaid: QuarterlyData;
  netOperatingCashflow: QuarterlyData;
  fixedAssetPurchases: QuarterlyData;
  netInvestingCashflow: QuarterlyData;
  equityInjection: QuarterlyData;
  loanDrawdowns: QuarterlyData;
  loanRepayments: QuarterlyData;
  interestPaid: QuarterlyData;
  dividendsPaid: QuarterlyData;
  netFinancingCashflow: QuarterlyData;
  netCashflow: QuarterlyData;
  openingCash: QuarterlyData;
  closingCash: QuarterlyData;
}

export interface BalanceSheetComputed {
  fixedAssetsGross: QuarterlyData;
  accumulatedDepreciation: QuarterlyData;
  adjDepreciation: QuarterlyData;
  fixedAssetsNet: QuarterlyData;
  tradeDebtors: QuarterlyData;
  vatReceivable: QuarterlyData;
  cashAtBank: QuarterlyData;
  totalCurrentAssets: QuarterlyData;
  totalAssets: QuarterlyData;
  tradeCreditors: QuarterlyData;
  corporationTaxLiability: QuarterlyData;
  totalCurrentLiabilities: QuarterlyData;
  longTermDebt: QuarterlyData;
  totalLiabilities: QuarterlyData;
  shareCapital: QuarterlyData;
  retainedEarnings: QuarterlyData;
  totalEquity: QuarterlyData;
  totalLiabilitiesAndEquity: QuarterlyData;
  balanceCheck: QuarterlyData;
}

export interface DebtScheduleComputed {
  openingBalance: QuarterlyData;
  drawdowns: QuarterlyData;
  repayments: QuarterlyData;
  closingBalance: QuarterlyData;
  interestExpense: QuarterlyData;
}

export interface ControlAccountComputed {
  equityOpening: QuarterlyData;
  equityInvestment: QuarterlyData;
  equityClosing: QuarterlyData;
  fixedAssetsOpening: QuarterlyData;
  fixedAssetsAdditions: QuarterlyData;
  fixedAssetsDepreciation: QuarterlyData;
  fixedAssetsDepreciationAdj: QuarterlyData;
  fixedAssetsClosing: QuarterlyData;
  debtorsOpening: QuarterlyData;
  debtorsInvoiced: QuarterlyData;
  debtorsAddVat: QuarterlyData;
  debtorsReceipts: QuarterlyData;
  debtorsAdjClosing: QuarterlyData;
  debtorsClosing: QuarterlyData;
  creditorsOpening: QuarterlyData;
  creditorsIncurred: QuarterlyData;
  creditorsAddVat: QuarterlyData;
  creditorsPayments: QuarterlyData;
  creditorsAdjClosing: QuarterlyData;
  creditorsClosing: QuarterlyData;
  vatOpening: QuarterlyData;
  vatCharged: QuarterlyData;
  vatRecovered: QuarterlyData;
  vatClosing: QuarterlyData;
  taxOpening: QuarterlyData;
  taxCharge: QuarterlyData;
  taxPayments: QuarterlyData;
  taxClosing: QuarterlyData;
}

export interface SensitivityCase {
  label: string;
  totalRevenue: number;
  totalCosts: number;
  grossProfit: number;
  ebitda: number;
  profitAfterTax: number;
  closingCash: number;
  netWorth: number;
  /**
   * The blended enterprise value under this scenario.
   *
   * Optional because v1 shares this contract and does not compute one. In v2
   * it is what lets the two valuation charts say something about a model that
   * has never had an AI forecast run against it.
   */
  enterpriseValue?: number | null;
}

export interface SensitivityComputed {
  baseCase: SensitivityCase;
  scenarios: SensitivityCase[];
  /**
   * A fixed ±1% shock per driver, for the valuation tornado.
   *
   * Separate from `scenarios`, which is whatever the model shows in its
   * sensitivity table and is usually ±10% - too far to read as elasticity,
   * since a thin-margin company's enterprise value can go negative there.
   */
  valuationScenarios?: SensitivityCase[];
}

export interface AnnualSummaryYear {
  totalRevenue: number;
  cogs: number;
  grossProfit: number;
  totalIndirectCosts: number;
  ebitda: number;
  depreciation: number;
  interestExpense: number;
  profitBeforeTax: number;
  corporationTax: number;
  profitAfterTax: number;
  grossMargin: number;
  ebitdaMargin: number;
  netMargin: number;
}

export interface AnnualSummaryComputed {
  years: Record<number, AnnualSummaryYear>;
}

export interface WhatIfScenarioResult {
  scenarioName: string;
  annualSummary: Record<number, AnnualSummaryYear>;
  closingCash: number;
  netWorth: number;
}

export interface WhatIfComputed {
  baseCase: WhatIfScenarioResult;
  scenarios: WhatIfScenarioResult[];
}

export interface DCFDetail {
  year: number;
  ebitda: number;
  capex: number;
  changeInWorkingCapital: number;
  taxesPaid: number;
  fcff: number;
  discountFactor: number;
  presentValue: number;
}

export interface DCFComputed {
  annualCashFlows: DCFDetail[];
  sumOfPVCashFlows: number;
  terminalValue: number;
  pvTerminalValue: number;
  enterpriseValue: number;
  netDebt: number;
  equityValue: number;
  impliedSharePrice: number;
}

export interface MultiplesComputed {
  terminalEBITDA: number;
  exitMultiple: number;
  enterpriseValue: number;
  netDebt: number;
  equityValue: number;
  impliedSharePrice: number;
}

export interface ComparableCheck {
  metric: string;
  multipleUsed: number;
  baseValue: number;
  impliedEV: number;
}

export interface ValuationSummary {
  dcfEnterpriseValue: number | null;
  dcfEquityValue: number | null;
  dcfSharePrice: number | null;
  multiplesEnterpriseValue: number | null;
  multiplesEquityValue: number | null;
  multiplesSharePrice: number | null;
  blendedEnterpriseValue: number;
  blendedEquityValue: number;
  blendedSharePrice: number;
}

/** One valuation method: a number, or what it would need to produce one. */
export interface MethodResult {
  method: string;
  label: string;
  /** null where the method could not be run. */
  equityValue: number | null;
  perShare: number | null;
  /** How it was arrived at, for a reader who wants to argue with it. */
  basis: string;
  /** Set where the method cannot run: what is missing. */
  needs: string | null;
  /** One of the anchors for this business type. */
  recommended: boolean;
  /** The commodity methods' answer along other price paths; empty for the rest. */
  scenarios?: { name: string; equityValue: number }[];
}

export interface MethodsComputed {
  businessType: BusinessType;
  /** What the business runs on, in a phrase. */
  engine: string;
  why: string;
  results: MethodResult[];
}

export interface ValuationComputed {
  dcf: DCFComputed | null;
  multiples: MultiplesComputed | null;
  comparables: ComparableCheck[];
  summary: ValuationSummary;
  /**
   * Written for the person valuing the company: a figure the engine refused to
   * credit, and why. A terminal value silently dropped is as misleading as a
   * negative one shown.
   */
  warnings?: string[];
}

export interface ModelComputed {
  pnl: PnLComputed;
  cashflows: CashflowComputed;
  balanceSheet: BalanceSheetComputed;
  debtSchedule: DebtScheduleComputed;
  controlAccount: ControlAccountComputed;
  sensitivity: SensitivityComputed;
  annualSummary: AnnualSummaryComputed;
  whatIf: WhatIfComputed;
  valuation: ValuationComputed;
}

// ─── Computed-cell overrides ────────────────────────────────

export type OverrideCategory = 'pnl' | 'cashflows' | 'balanceSheet';
export type OverrideBucket = Record<string, Record<string, number>>;

export interface ComputedOverrides {
  pnl: OverrideBucket;
  cashflows: OverrideBucket;
  balanceSheet: OverrideBucket;
}

export function emptyOverrides(): ComputedOverrides {
  return { pnl: {}, cashflows: {}, balanceSheet: {} };
}

export function hasOverride(
  overrides: ComputedOverrides | undefined,
  category: OverrideCategory,
  field: string,
  period: string,
): boolean {
  if (!overrides) return false;
  return overrides[category]?.[field]?.[period] !== undefined;
}

// ─── Historical Data ────────────────────────────────────────

export interface HistoricalPnL {
  revenue: number;
  costOfSales: number;
  grossProfit: number;
  distributionCosts: number;
  administrativeExpenses: number;
  otherOperatingIncome: number;
  operatingProfit: number;
  interestReceivable: number;
  interestPayable: number;
  profitBeforeTax: number;
  taxation: number;
  profitAfterTax: number;
  dividends: number;
  retainedProfit: number;
  totalStaffCosts: number;
  depreciation: number;
  amortisation: number;
  averageHeadcount: number;
}

export interface HistoricalBalanceSheet {
  intangibleAssets: number;
  tangibleAssets: number;
  investments: number;
  totalFixedAssets: number;
  stocks: number;
  tradeDebtors: number;
  otherDebtors: number;
  cashAtBank: number;
  totalCurrentAssets: number;
  tradeCreditors: number;
  otherCreditors: number;
  shortTermBorrowings: number;
  totalCurrentLiabilities: number;
  netCurrentAssets: number;
  totalAssetsLessCurrentLiabilities: number;
  longTermBorrowings: number;
  provisions: number;
  otherLongTermLiabilities: number;
  totalLongTermLiabilities: number;
  netAssets: number;
  shareCapital: number;
  sharePremium: number;
  retainedEarnings: number;
  otherReserves: number;
  totalEquity: number;
}

export interface HistoricalCashflow {
  operatingProfit: number;
  depreciationAmortisation: number;
  changeInWorkingCapital: number;
  taxPaid: number;
  otherOperatingItems: number;
  netOperatingCashflow: number;
  purchaseOfFixedAssets: number;
  saleOfFixedAssets: number;
  purchaseOfInvestments: number;
  otherInvestingItems: number;
  netInvestingCashflow: number;
  equityRaised: number;
  loanDrawdowns: number;
  loanRepayments: number;
  interestPaid: number;
  dividendsPaid: number;
  otherFinancingItems: number;
  netFinancingCashflow: number;
  netChangeInCash: number;
  openingCash: number;
  closingCash: number;
}

export interface HistoricalYear {
  year: number;
  yearEndDate: string;
  pnl: HistoricalPnL;
  balanceSheet: HistoricalBalanceSheet;
  cashflow: HistoricalCashflow;
}

export interface HistoricalData {
  years: Record<number, HistoricalYear>;
}

// ─── Import Report ──────────────────────────────────────────

export interface ImportReportItem {
  category: string;
  field: string;
  label: string;
  status: 'imported' | 'missing' | 'defaulted' | 'mismatch';
  excelValue?: number | null;
  appValue?: number | null;
  note: string;
}

// ─── Top-level Financial Model ───────────────────────────────

export interface FinancialModel {
  id: string;
  tenantId: string;
  ownerId: string;
  name: string;
  description: string;
  settings: ModelSettings;
  inputs: ModelInputs;
  computed: ModelComputed;
  overrides?: ComputedOverrides;
  aiSummary?: string | null;
  aiBalanceAnalysis?: string | null;
  aiLearnings?: string | null;
  historicalData?: HistoricalData | null;
  extractedInputs?: ModelInputs | null;
  extractedSettings?: ModelSettings | null;
  importReport?: ImportReportItem[] | null;
  lastAutocorrectResult?: AutocorrectResult | null;
  /** Snapshots of inputs per period type (monthly/quarterly/annual) so
   * round-trip conversions restore the user's original values. */
  inputCache?: Record<string, ModelInputs>;
  /** v2 forecasting strategy. Defaults to llm_single_shot for existing
   * models. See docs/forecasting/proposed-engine.md. */
  forecastStrategy?: ForecastStrategy;
  /** Cached output of the most recent llm_bayes_mc run. */
  forecastDistribution?: ForecastDistributionPayload | null;
  /** Most recently-supplied forecast context for models without historicals.
   * Persisted so the v2 panel can pre-populate the form on reload. */
  forecastContextInput?: ForecastContextInputPayload | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

// ─── v2 Forecasting Types ───────────────────────────────────

export type ForecastStrategy = 'llm_single_shot' | 'llm_bayes_mc' | 'manual';

export interface ForecastDistributionPayload {
  enterpriseValue: {
    median: number;
    mean: number;
    std: number;
    p5: number;
    p25: number;
    p50: number;
    p75: number;
    p95: number;
  };
  sensitivity: Record<string, number>;
  forecasterSummaries: Record<string, Record<string, unknown>>;
  forecasterWarnings: string[];
  posteriorSummaries: Record<string, { median: number; p10: number; p90: number; n: number }>;
  cacheHit: boolean;
  nSamples: number;
  promptVersion: string;
  /** Markdown explanation of how the forecast was produced — sources,
   * per-forecaster method, posterior summaries, DCF config, sensitivity. */
  methodology?: string;
  /** Period-keyed forecast values per variable. Mirrors ModelInputs structure
   * — can be deep-merged into inputs via the /apply endpoint. Shape:
   *   {
   *     revenue: { sales1: { FY_2026: 800, ... }, sales2: ..., ... },
   *     expenses: { marketing: { FY_2026: 50, ... }, ... },
   *     staffing: { operationsRiskTech: { headcount: ..., costPerHead: ... }, ... },
   *     controlAccount: { fixedAssetAdditions: { FY_2026: 25, ... } }
   *   }
   */
  inputsPatch?: Record<string, Record<string, unknown>>;
}

/** User-supplied forecast context, persisted on the model. Same shape that
 * the v2 panel form collects when historicalData is missing. */
export interface ForecastContextInputPayload {
  country: string;
  sectorId: string | null;
  lastRevenue: number | null;
  lastHeadcount: number | null;
  lastStaffCost: number | null;
  lastDepreciation: number | null;
  lastCapex: number | null;
  narrative: string | null;
  peerHints: string[] | null;
}

// ─── Autocorrect Types ──────────────────────────────────────

export interface AutocorrectChange {
  field: string;
  oldValue: number;
  newValue: number;
  justification: string;
  sheetId: SheetId;
  displayName: string;
}

export interface AutocorrectResult {
  applied: boolean;
  changes: AutocorrectChange[];
  summary: string;
  residualExplanation: string;
  newBalanceCheck: Record<string, number>;
}

// ─── Metadata Types ─────────────────────────────────────────

export interface ModelMetadata {
  id: string;
  ownerId: string;
  name: string;
  description: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ModelVersionMeta {
  version: number;
  name: string;
  scenarioName?: string | null;
  createdAt: string;
}
